import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface ToastProps {
	message: string;
	type?: 'success' | 'error' | 'info';
	duration?: number;
	onClose: () => void;
}

export function Toast({ message, type = 'success', duration = 3000, onClose }: ToastProps) {
	const [isVisible, setIsVisible] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsVisible(false);
			setTimeout(onClose, 300); // Wait for fade out animation
		}, duration);

		return () => clearTimeout(timer);
	}, [duration, onClose]);

	const getIcon = () => {
		switch (type) {
			case 'success':
				return <CheckCircle className="w-5 h-5 flex-shrink-0" />;
			case 'error':
				return <AlertCircle className="w-5 h-5 flex-shrink-0" />;
			case 'info':
				return <Info className="w-5 h-5 flex-shrink-0" />;
			default:
				return <CheckCircle className="w-5 h-5 flex-shrink-0" />;
		}
	};

	const getStyles = () => {
		switch (type) {
			case 'success':
				return 'bg-green-600 text-white';
			case 'error':
				return 'bg-red-600 text-white';
			case 'info':
				return 'bg-blue-600 text-white';
			default:
				return 'bg-green-600 text-white';
		}
	};

	return (
		<div
			className={`fixed top-4 right-4 z-[100] max-w-sm transition-all duration-300 ${
				isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
			}`}
		>
			<div
				className={`${getStyles()} px-4 py-3 rounded-lg shadow-lg flex items-center gap-3`}
			>
				{getIcon()}
				<span className="text-sm font-medium flex-1">{message}</span>
				<button
					onClick={() => {
						setIsVisible(false);
						setTimeout(onClose, 300);
					}}
					className="ml-2 opacity-70 hover:opacity-100 transition-opacity"
				>
					<X className="w-4 h-4" />
				</button>
			</div>
		</div>
	);
}
